import { Container } from 'components/container'
import styled, { css, keyframes } from 'styled-components'
import { styleReset } from 'styles/misc'
import { mq } from 'styles/mq'
import { theme } from 'styles/theme'

export const Title = styled.h1`
  margin: 0 0 2rem;
  font: 600 clamp(3rem, 12vw, 5.5rem) ${theme.fonts.headings};
  text-transform: uppercase;

  ${mq.medium} {
    font-size: 5.5rem;
  }
`

export const Introduction = styled.div`
  margin-bottom: 4em;
  text-align: justify;
  margin-bottom: 0.4em;
  list-style-type: none;
`

export const ReadMore = styled.button`
  ${styleReset}
  display: block;
  margin: 0.4rem 0 0 auto;
  text-decoration: underline;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: var(--color-primary);
  }
`

export const Cols = styled.div`
  display: flex;
  align-items: flex-start;
  overflow: hidden;
`

export const Video = styled.video`
  flex-shrink: 0;
  align-self: center;
  pointer-events: none;
  mask: linear-gradient(to top, transparent, black 25%);

  ${mq.smallOnly} {
    display: none;
  }

  ${mq.medium} {
    width: 50vw;
    margin: -10vw -10vw 0 0;
  }

  ${mq.large} {
    width: 70rem;
    margin: -15rem -15rem 0 0;
  }
`

export const Header = styled.header`
  padding: 4rem 0;
  overflow: hidden;

  ${mq.medium} {
    padding: 8rem 0 0;
  }

  ${Container} {
    position: relative;
    z-index: 0;
  }

  ${Title}, ${Introduction} {
    max-width: 82rem;
  }
`

export const Featured = styled.div<{ slideGap: number; slideDuration: number }>`
  --slide-width: calc(100% - 4rem);
  --slide-gap: ${(props) => props.slideGap / 10}rem;
  --slide-duration: ${(props) => props.slideDuration}s;
  --swiper-theme-color: white;
  --swiper-pagination-bullet-inactive-color: white;
  --swiper-pagination-bullet-inactive-opacity: 0.2;
  --swiper-pagination-bullet-size: 1.5rem;

  ${mq.medium} {
    --slide-width: 40rem;
    --slide-gap: ${(props) => props.slideGap / 10}rem;
  }

  position: relative;

  .swiper {
    display: flex;
    flex-direction: column;
  }

  .swiper-slide {
    width: var(--slide-width);

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .swiper-button-prev,
  .swiper-button-next {
    margin-left: calc(-5rem - var(--slide-gap));
    margin-right: calc(-5rem - var(--slide-gap));
  }

  .swiper-button-prev {
    left: calc(50% - (var(--slide-width) / 2));
  }

  .swiper-button-next {
    right: calc(50% - (var(--slide-width) / 2));
  }

  .swiper-pagination {
    --swiper-theme-color: var(--color);
    --swiper-pagination-bullet-inactive-color: var(--color);

    position: relative;
    left: auto;
    bottom: auto;
    margin-top: var(--slide-gap);
    order: 10;
  }
`

export const SlideContent = styled.div`
  --slide-transition-duration: 0.5s;

  position: relative;
  display: flex;
  flex-direction: column;
  height: 55rem;
  padding: 3rem 0;
  transition: all var(--slide-transition-duration);
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    flex: 1;
    object-fit: cover;
  }

  &::after {
    content: '';
    display: block;
    height: 0;
    transition: all var(--slide-transition-duration);
  }

  .swiper-slide-active & {
    padding: 0;

    &::after {
      height: 20rem;
    }
  }
`

export const Detail = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  padding: 3rem;
  color: var(--background);
  text-align: center;
  background: var(--color);
  transition: all var(--slide-transition-duration);

  > :first-child {
    margin-top: 0;
  }

  > :last-child {
    margin-bottom: 0;
  }

  .swiper-slide-active & {
    transform: translateY(-100%);
  }

  h3 {
    font-size: 3rem;
    font-weight: normal;
    margin: 0;
    line-height: 1.1;
  }

  p {
    font-size: 1.8rem;
    margin: 1rem 0 0;
    line-height: 1.2;
  }

  a {
    display: block;
    padding: 1.2rem;
    margin-top: 1rem;
    color: var(--color);
    font-size: 1.8rem;
    text-decoration: none;
    text-transform: uppercase;
    background: var(--background);

    &:hover,
    &:active {
      opacity: 0.8;
    }
  }
`

const progressAnimation = keyframes`
  from { width: 0; }
  to   { width: 100%; }
`

export const Progress = styled.div<{ isPaused: boolean }>`
  position: absolute;
  top: 0;
  left: 50%;
  width: calc(var(--slide-width) - 2 * var(--slide-gap));
  margin: var(--slide-gap) calc(-0.5 * var(--slide-width) + var(--slide-gap));
  transition: 0.3s opacity;
  background: var(--background);
  z-index: 10;

  &::before {
    content: '';
    display: block;
    height: 5px;
    width: 50%;
    animation: calc(var(--slide-duration) + 0.5s) ${progressAnimation} linear
      forwards;
    background-color: var(--color-primary);
  }

  ${(props) =>
    props.isPaused &&
    css`
      opacity: 0;
      pointer-events: none;

      &::before {
        animation-name: none;
      }
    `}
`
